import React, { useState, useRef, useEffect } from 'react';
import { Modal, Radio, Input, Spin, Row, Col, Typography, Image, message, Button } from 'antd';
import { postCall } from '../../utils/network.util';
import { CREATE_UPI_RECHARGE_URL, GET_UPI_RECHARGE_STATUS_URL } from '../../utils/api-url';
import { isValid } from '../../utils/utilities';
import { getOrderIdTimestamp } from '../../utils/date.utils';
import { PRE_PAID_USER } from '../../utils/constant';
import './upi-booking.style.scss';

const { Text } = Typography;

// UPI modes - must match the travels server constants.
const UPI_MODE_QR = 'QR';
const UPI_MODE_LINK = 'LINK';

const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

/*
  Gating: a prepaid "agent" of an operator that has UPI booking enabled gets the
  recharge experience (UPI wallet top-up) on the agent-transactions page.
*/
export const isRechargeAgent = () =>
  isValid(global.travelsAppConfig) &&
  global.travelsAppConfig['ENABLE_UPI_BOOKING'] == 1 &&
  global.agentType === PRE_PAID_USER &&
  isValid(global.privilege) &&
  String(global.privilege).toLowerCase().includes('agent');

/*
  UPI wallet recharge modal. Given an amount, raises the chosen UPI instrument
  (QR / mobile link) and polls until the wallet is credited, then onSuccess().
*/
export const UpiRechargeModal = ({ visible, amount, onClose, onSuccess }) => {
    const [mode, setMode] = useState(UPI_MODE_QR);
    const [mobile, setMobile] = useState('');
    const [stage, setStage] = useState('select'); // 'select' | 'awaiting'
    const [loading, setLoading] = useState(false);
    const [reference, setReference] = useState(null);

    const pollRef = useRef(null);
    const pollTimeoutRef = useRef(null);

    const clearPolling = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
    };

    useEffect(() => () => clearPolling(), []);

    const resetState = () => {
        clearPolling();
        setStage('select');
        setLoading(false);
        setReference(null);
        setMobile('');
        setMode(UPI_MODE_QR);
    };

    const closeModal = () => {
        resetState();
        onClose();
    };

    const beginPolling = (orderId, currentReference) => {
        const startedAt = Date.now();
        pollRef.current = setInterval(async () => {
            try {
                const response = await postCall(GET_UPI_RECHARGE_STATUS_URL, {
                    orderId,
                    reference: { ...currentReference, amount },
                    paymentFor: 'recharge'
                });
                if (response && response.status === 'SUCCESS' && isValid(response.payload)
                    && response.payload.Status === 'SUCCESS' && isValid(response.payload.Payload)) {
                    const paymentStatus = response.payload.Payload.status;
                    if (paymentStatus === 'Confirmed') {
                        clearPolling();
                        message.success('Recharge successful - wallet credited!');
                        closeModal();
                        onSuccess();
                    } else if (paymentStatus === 'Pay_Success_Book_Fail') {
                        clearPolling();
                        const reason = response.payload.Payload.reason;
                        const detail = response.payload.Payload.detail;
                        console.error('UPI recharge credit failed', reason, detail);
                        message.error(`Payment received but wallet credit failed${reason ? ` (${reason})` : ''}. Please contact support.`);
                        setStage('select');
                    }
                }
            } catch (err) {
                console.error('UPI recharge status poll failed', err);
            }
            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                clearPolling();
                message.warning('Recharge not completed in time. Please try again.');
                setStage('select');
            }
        }, POLL_INTERVAL_MS);
    };

    const startRecharge = async () => {
        if (!isValid(amount) || Number(amount) <= 0) {
            message.error('Please enter a valid recharge amount');
            return;
        }
        if (mode === UPI_MODE_LINK && !isValid(mobile)) {
            message.error('Please enter the mobile number');
            return;
        }
        setLoading(true);
        try {
            const orderId = `RCH-${global.operatorCode}-${global.agentId}-${getOrderIdTimestamp()}`;
            const createResponse = await postCall(CREATE_UPI_RECHARGE_URL, {
                orderId,
                mode,
                amount,
                mobile: mode === UPI_MODE_LINK ? mobile : undefined,
                customerName: global.userName,
                paymentFor: 'recharge'
            });
            if (createResponse && createResponse.status === 'SUCCESS' && isValid(createResponse.payload)
                && createResponse.payload.Status === 'SUCCESS' && isValid(createResponse.payload.Payload)) {
                const ref = createResponse.payload.Payload;
                setReference(ref);
                setStage('awaiting');
                setLoading(false);
                beginPolling(orderId, ref);
            } else {
                const errorMessage = createResponse && isValid(createResponse.payload) && isValid(createResponse.payload.Message)
                    ? createResponse.payload.Message
                    : 'Failed to initiate recharge';
                message.error(errorMessage);
                setLoading(false);
            }
        } catch (err) {
            console.error('UPI recharge failed', err);
            message.error(isValid(err.message) ? err.message : 'Failed to initiate recharge');
            setLoading(false);
        }
    };

    const renderSelect = () => (
        <div>
            <Row className="upi-booking-amount">
                <Col><Text strong>Recharge amount : </Text>{amount}</Col>
            </Row>
            <Radio.Group className="upi-booking-modes" value={mode} onChange={(e) => setMode(e.target.value)}>
                <Radio value={UPI_MODE_QR}>Show QR</Radio>
                <Radio value={UPI_MODE_LINK}>Via mobile number</Radio>
            </Radio.Group>
            {mode === UPI_MODE_LINK &&
                <Row className="upi-booking-input"><Col span={24}>
                    <Input placeholder="Mobile number" maxLength={10} value={mobile} onChange={(e) => setMobile(e.target.value)} />
                </Col></Row>
            }
        </div>
    );

    const renderAwaiting = () => (
        <div className="upi-booking-awaiting">
            {mode === UPI_MODE_QR && reference && isValid(reference.imageUrl) &&
                <Row justify="center"><Col>
                    <Image src={reference.imageUrl} alt="UPI QR" width={240} preview={false} />
                    <div className="upi-booking-hint">Scan the QR with any UPI app to pay {amount}</div>
                </Col></Row>
            }
            {mode === UPI_MODE_LINK &&
                <div className="upi-booking-hint">
                    A UPI payment link for {amount} has been sent over SMS to <Text strong>{mobile}</Text>.
                    {reference && isValid(reference.shortUrl) && <div><a href={reference.shortUrl} target="_blank" rel="noreferrer">{reference.shortUrl}</a></div>}
                </div>
            }
            <Row justify="center" className="upi-booking-waiting-row"><Col><Spin /> <Text>Waiting for payment confirmation…</Text></Col></Row>
        </div>
    );

    return (
        <Modal
            title="UPI Recharge"
            visible={visible}
            onCancel={closeModal}
            maskClosable={false}
            footer={stage === 'select' ? [
                <Button key="cancel" className="exit-btn" onClick={closeModal}>Exit</Button>,
                <Button key="ok" type="primary" loading={loading} onClick={startRecharge}>Recharge</Button>
            ] : [
                <Button key="close" className="exit-btn" onClick={closeModal}>Close</Button>
            ]}
        >
            {stage === 'select' ? renderSelect() : renderAwaiting()}
        </Modal>
    );
};
